import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../api/config';
import { Card, CardContent, Button, Input, Alert } from '@license-management/ui';
import { Lock, AlertCircle, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Unauthenticated endpoint: apiClient.post() only triggers the
      // 401 -> refresh -> retry flow when an access token is present
      // (see ApiClient.fetch in ../api/config), so a stray portal
      // session in localStorage can't hijack this request or send us
      // through a login redirect on failure.
      await apiClient.post(API_ENDPOINTS.resetPassword, { token, newPassword: password });

      setIsSuccess(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password reset failed';
      // apiClient falls back to a generic 'Request failed' when the
      // response body can't be parsed; keep this page's original,
      // more specific fallback copy in that case.
      setError(message === 'Request failed' ? 'Password reset failed' : message);
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-100 text-red-600 mb-4">
              <XCircle className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Invalid Reset Link
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              This password reset link is invalid or has expired.
            </p>
          </div>

          <Card shadow="elevated">
            <CardContent className="p-8 text-center">
              <Button asChild variant="secondary" className="w-full">
                <Link to="/login">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Return to login
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-green-100 text-green-600 mb-4">
              <CheckCircle className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Password Reset Successful
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Your password has been updated. You can now log in with your new password.
            </p>
          </div>

          <Card shadow="elevated">
            <CardContent className="p-8">
              <Button asChild className="w-full" size="lg">
                <Link to="/login">Go to Login</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary-600 text-white mb-4 shadow-lg shadow-primary-600/25">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Reset Your Password
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Enter your new password below.
          </p>
        </div>

        {/* Reset Form Card */}
        <Card shadow="elevated">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert
                  variant="error"
                  icon={<AlertCircle className="h-5 w-5" />}
                >
                  {error}
                </Alert>
              )}

              <Input
                label="New Password"
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                helperText="Password must be at least 8 characters"
              />

              <Input
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />

              <Button
                type="submit"
                loading={isLoading}
                className="w-full"
                size="lg"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Back to login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
